<template>
  <header class="home-header">
    <div class="home-header__content">
      <!-- 左侧品牌区域 -->
      <div class="brand-section" @click="handleBrandClick">
        <img
          class="brand-logo"
          src="@/assets/imgs/web-logo.png"
          alt="GreenValley Logo"
        />
      </div>

      <!-- 右侧功能区域 -->
      <div class="function-section">
        <!-- 未登录状态 (Visitor) -->
        <template v-if="isDashboardHeader || !isAuthed">
          <!-- Demo -->
          <div class="header-icon-btn" @click="handleDemo">
            <img :src="demoIcon" alt="Demo" />
            <span>{{ t("common.demo") }}</span>
          </div>

          <!-- Buy Now -->
          <div class="header-icon-btn" @click="handleBuyNowVisitor">
            <img :src="buyNowIcon" alt="Buy Now" />
            <span>{{ t("common.buyNow") }}</span>
          </div>

          <!-- Contact -->
          <div class="header-icon-btn" @click="handleContact">
            <img :src="contactIcon" alt="Contact" />
            <span>{{ t("common.contact") }}</span>
          </div>

          <!-- Log in -->
          <div class="header-icon-btn" @click="handleHomeUserEntryClick">
            <img :src="loginIcon" alt="Log in" />
            <span>{{ homeUserEntryText }}</span>
          </div>

          <!-- Language -->
          <div class="header-icon-btn language-btn">
            <img :src="languageIcon" alt="Language" />
            <span>{{
              isDashboardHeader ? currentLanguageLabel : t("common.language")
            }}</span>
            <!-- 简单的语言切换浮层 -->
            <div class="lang-dropdown">
              <div
                v-for="lang in appStore.langs"
                :key="lang.code"
                class="lang-item"
                :class="{ active: locale === lang.code }"
                @click.stop="onLangClick(lang.code)"
              >
                {{ lang.name }}
              </div>
            </div>
          </div>
        </template>

        <!-- 已登录状态 -->
        <template v-else>
          <!-- 试用用户：显示 Buy Now 按钮 -->
          <template v-if="!authStore.isSubscribed">
            <div class="buy-now-btn" @click="handleBuyNow">
              <span class="buy-now-text">{{ t("common.buyNow") }}</span>
            </div>
          </template>

          <!-- 付费用户：显示存储进度和 Upgrade 按钮 -->
          <template v-else>
            <!-- 存储进度背景 -->
            <div class="storage-bg" ref="storageBgRef">
              <!-- 存储图标 -->
              <img
                class="storage-icon"
                src="@/assets/imgs/header/storage-icon.png"
                alt="Storage"
              />

              <!-- 存储信息 -->
              <div class="storage-info">
                <div class="storage-text">
                  <!-- <span class="storage-percent">{{ storagePercent }}%</span> -->
                  <span class="storage-capacity"
                    >{{ storageUsed }}/{{ storageTotal }}</span
                  >
                </div>
                <div class="storage-progress" aria-hidden="true">
                  <div
                    class="storage-progress__fill"
                    :style="{ width: `${storagePercent}%` }"
                  ></div>
                </div>
              </div>

              <!-- 分隔线 -->
              <img
                class="divider-vertical"
                src="@/assets/imgs/header/divider-vertical.png"
                alt="Divider"
              />

              <!-- 任务文字 -->
              <div
                ref="taskTriggerRef"
                class="task-trigger"
                @mouseenter="handleTaskTriggerEnter"
                @mouseleave="handleTaskTriggerLeave"
              >
                <span class="tasks-text">{{ t("common.performTasks") }}</span>

                <!-- 任务数量 -->
                <span class="tasks-count">
                  {{ taskCount }}
                </span>
              </div>
            </div>

            <!-- Upgrade 按钮 -->
            <div v-if="authStore.isOrgAdmin" class="upgrade-btn" @click="handleUpgrade">
              <img
                class="upgrade-icon"
                src="@/assets/imgs/header/btn-upgrade-icon.png"
                alt="Upgrade"
              />
              <span class="upgrade-text">{{ t("common.upgrade") }}</span>
            </div>
          </template>

          <!-- 消息通知图标 - 放在用户信息外部平级位置 -->
          <div class="notification-icon" @click.stop="toggleNotification">
            <img
              :src="hasNotification ? notificationWithBadge : notificationEmpty"
              alt="Notification"
            />
          </div>

          <!-- 用户信息区域 -->
          <a-dropdown
            :trigger="['click']"
            @visible-change="onDropdownVisibleChange"
          >
            <div
              class="user-section"
              :class="{ 'user-section--active': isDropdownOpen }"
            >
              <img class="user-icon" :src="userIconSrc" alt="User" />
              <span class="user-name">{{ displayName }}</span>
            </div>
            <template #overlay>
              <div class="user-dropdown-menu">
                <!-- Account -->
                <div class="menu-item" @click="handleAccount">
                  <img
                    class="menu-icon"
                    src="@/assets/imgs/header/menu-account-icon.png"
                    alt="Account"
                  />
                  <span class="menu-text">{{ t("common.account") }}</span>
                </div>

                <!-- Language (可展开的子菜单) -->
                <div class="menu-item-with-submenu">
                  <div class="menu-item" @click.stop="toggleLanguageSubmenu">
                    <img
                      class="menu-icon"
                      src="@/assets/imgs/header/menu-language-icon.png"
                      alt="Language"
                    />
                    <span class="menu-text">{{ t("common.language") }}</span>
                    <img
                      class="menu-arrow-icon"
                      :class="{
                        'menu-arrow-icon--expanded': languageSubmenuOpen,
                      }"
                      src="@/assets/imgs/header/menu-arrow-icon.png"
                      alt="Arrow"
                    />
                  </div>
                  <!-- 语言子菜单 -->
                  <transition name="submenu-expand">
                    <div v-show="languageSubmenuOpen" class="language-submenu">
                      <div
                        v-for="lang in appStore.langs"
                        :key="lang.code"
                        class="submenu-item"
                        :class="{
                          'submenu-item--active': locale === lang.code,
                        }"
                        @click.stop="onLangClick(lang.code)"
                      >
                        <span class="submenu-text">{{ lang.name }}</span>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- User Manage (仅组织管理员可见) -->
                <div
                  v-if="authStore.isOrgAdmin"
                  class="menu-item"
                  @click="handleUserManage"
                >
                  <img
                    class="menu-icon"
                    src="@/assets/imgs/header/icon-yonghuguanli@2x.png"
                    :alt="t('common.userManage')"
                  />
                  <span class="menu-text">{{ t("common.userManage") }}</span>
                </div>

                <!-- Demo -->
                <div class="menu-item" @click="handleDemo">
                  <img
                    class="menu-icon"
                    src="@/assets/imgs/header/menu-demo-icon.png"
                    alt="Demo"
                  />
                  <span class="menu-text">{{ t("common.demo") }}</span>
                </div>

                <!-- 分隔线 -->

                <!-- Log out -->
                <div class="menu-item logout-item" @click="onLogout">
                  <img
                    class="menu-icon"
                    src="@/assets/imgs/header/menu-logout-icon.png"
                    alt="Logout"
                  />
                  <span class="menu-text">{{ t("common.logout") }}</span>
                </div>
              </div>
            </template>
          </a-dropdown>
        </template>
      </div>
    </div>

    <!-- 底部分隔线 -->
    <img
      class="divider-horizontal"
      src="@/assets/imgs/header/divider-horizontal.png"
      alt="Divider"
    />

    <!-- 任务面板 -->
    <Teleport to="body">
      <TaskPanel
        v-if="showTaskPanel && taskCount > 0"
        ref="taskPanelRef"
        :position="taskPanelPosition"
        :tasks="runningTasks"
        @mouseenter="handleTaskPanelEnter"
        @mouseleave="handleTaskPanelLeave"
      />
    </Teleport>
    <AccountSettingDrawer
      :open="accountDrawerOpen"
      :user="accountSettingUser"
      :loading="accountDrawerLoading"
      :submit-loading="accountDrawerSubmitting"
      @cancel="handleAccountDrawerCancel"
      @confirm="handleAccountDrawerConfirm"
    />
  </header>
</template>

<script setup>
import {
  computed,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "../store/auth";
import { useAppStore } from "../store/app";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import TaskPanel from "./header/TaskPanel.vue";
import AccountSettingDrawer from "./header/AccountSettingDrawer.vue";
import { getGlobalRunningTasks } from "@/api/task";
import { updateUser } from "@/api/user";
import userIconDefault from "@/assets/imgs/header/user-icon-default.png";
import userIconActive from "@/assets/imgs/header/user-icon.png";
import notificationEmpty from "@/assets/imgs/header/notification-icon-empty.png";
import notificationWithBadge from "@/assets/imgs/header/notification-icon-with-badge.png";

// New Icons
import demoIcon from "@/assets/imgs/header/demo.png";
import buyNowIcon from "@/assets/imgs/header/buy_now.png";
import contactIcon from "@/assets/imgs/header/contact.png";
import loginIcon from "@/assets/imgs/header/login.png";
import languageIcon from "@/assets/imgs/header/language.png";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const appStore = useAppStore();
const { t, locale } = useI18n();

// 计算属性
const isDashboardHeader = computed(() => route.name === "Dashboard");
const isAuthed = computed(() => authStore.isAuthed);
const displayName = computed(
  () =>
    authStore.user?.fullName ||
    authStore.user?.name ||
    authStore.user?.emailAddress ||
    authStore.user?.email ||
    "Admin",
);
const homeUserEntryText = computed(() =>
  isDashboardHeader.value && isAuthed.value
    ? displayName.value
    : t("common.login"),
);
const currentLanguageLabel = computed(() => {
  const activeLang = appStore.langs.find((item) => item.code === locale.value);
  return activeLang?.name || t("common.language");
});
const accountDrawerOpen = ref(false);
const accountDrawerLoading = ref(false);
const accountDrawerSubmitting = ref(false);
const accountSettingUser = computed(() => ({
  id: authStore.user?.id || authStore.user?.userId || "",
  nickname:
    authStore.user?.fullName ||
    authStore.user?.name ||
    authStore.user?.emailAddress ||
    authStore.user?.email ||
    "Admin",
  profileIcon: authStore.user?.profileIcon || "",
  avatarUrl: authStore.user?.avatarUrl || "",
  email:
    authStore.user?.emailAddress ||
    authStore.user?.email ||
    "admin@company.com",
  status: authStore.user?.status || "0",
  company: authStore.user?.company || "",
  industry: authStore.user?.industry || "",
}));

// 下拉框状态
const isDropdownOpen = ref(false);

// 消息通知状态（来自 store）
const hasNotification = computed(() => appStore.hasNotification);

// 切换消息状态（仅用于调试，实际逻辑在 store）
const toggleNotification = () => {
  appStore.toggleNotification();
};

// 根据下拉框状态切换用户图标
const userIconSrc = computed(() => {
  return isDropdownOpen.value ? userIconActive : userIconDefault;
});

// 存储信息（根据设计稿）
const packageInfo = computed(() => authStore.user?.packageInfo || null);
const formatStorageNumber = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) {
    return "0";
  }
  return Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2).replace(/\.?0+$/, "");
};
const storagePercent = computed(() => {
  const percent = Number(packageInfo.value?.usedPercent || 0);
  return Math.max(0, Math.min(100, Math.round(percent)));
});
const storageUsed = computed(() => {
  const units = packageInfo.value?.storageUnits || "GB";
  return `${formatStorageNumber(packageInfo.value?.usedSpace)}${units}`;
});
const storageTotal = computed(() => {
  const units = packageInfo.value?.storageUnits || "GB";
  return `${formatStorageNumber(packageInfo.value?.storageLimit)}${units}`;
});

// 任务面板逻辑
const showTaskPanel = ref(false);
const taskPanelRef = ref(null);
const storageBgRef = ref(null);
const taskTriggerRef = ref(null);
const taskPanelPosition = ref({ top: 64, right: 204 });
let taskPanelHideTimer = null;
let taskRefreshTimer = null;
const TASK_REFRESH_INTERVAL = 60000;
const runningTasks = ref([]);
const taskCount = computed(() => {
  // 如果有真实的任务数据，从 store 获取
  // 这里暂时使用 TaskPanel 暴露的数据
  return runningTasks.value.length;
});

// 切换任务面板（点击任务数量）
const loadRunningTasks = async () => {
  if (!authStore.isAuthed) {
    runningTasks.value = [];
    return;
  }

  try {
    const response = await getGlobalRunningTasks();

    if (response?.code !== 200) {
      throw new Error(response?.msg || "load running tasks failed");
    }

    runningTasks.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    console.error("[GlobalHeader] Failed to load running tasks:", error);
    runningTasks.value = [];
  }
};

const clearTaskRefreshTimer = () => {
  if (taskRefreshTimer) {
    clearInterval(taskRefreshTimer);
    taskRefreshTimer = null;
  }
};

const startTaskRefreshTimer = () => {
  clearTaskRefreshTimer();

  if (!authStore.isAuthed) {
    return;
  }

  taskRefreshTimer = window.setInterval(() => {
    loadRunningTasks();
  }, TASK_REFRESH_INTERVAL);
};

const updateTaskPanelPosition = () => {
  const target = taskTriggerRef.value || storageBgRef.value;
  if (!target) return;
  const rect = target.getBoundingClientRect();
  taskPanelPosition.value = {
    top: rect.bottom + 5,
    right: Math.max(0, window.innerWidth - rect.right),
  };
};

const clearTaskPanelHideTimer = () => {
  if (taskPanelHideTimer) {
    clearTimeout(taskPanelHideTimer);
    taskPanelHideTimer = null;
  }
};

const openTaskPanel = async () => {
  clearTaskPanelHideTimer();
  if (taskCount.value <= 0) {
    showTaskPanel.value = false;
    return;
  }
  if (!showTaskPanel.value) {
    showTaskPanel.value = true;
    await nextTick();
  }
  updateTaskPanelPosition();
};

const scheduleTaskPanelHide = () => {
  clearTaskPanelHideTimer();
  taskPanelHideTimer = window.setTimeout(() => {
    showTaskPanel.value = false;
    taskPanelHideTimer = null;
  }, 160);
};

const handleTaskTriggerEnter = () => {
  openTaskPanel();
};

const handleTaskTriggerLeave = () => {
  scheduleTaskPanelHide();
};

const handleTaskPanelEnter = () => {
  clearTaskPanelHideTimer();
};

const handleTaskPanelLeave = () => {
  scheduleTaskPanelHide();
};

const HOME_PRICING_HASH = "#home-pricing";
const HOME_CONTACT_HASH = "#home-contact";

const navigateToHomePricing = async () => {
  closeUserDropdown();

  if (route.name === "Dashboard") {
    if (route.hash !== HOME_PRICING_HASH) {
      await router.replace({
        name: "Dashboard",
        hash: HOME_PRICING_HASH,
      });
    }

    window.dispatchEvent(new CustomEvent("dashboard-scroll-to-pricing"));
    return;
  }

  await router.push({
    name: "Dashboard",
    hash: HOME_PRICING_HASH,
  });
};

const navigateToHomeContact = async () => {
  closeUserDropdown();

  if (route.name === "Dashboard") {
    if (route.hash !== HOME_CONTACT_HASH) {
      await router.replace({
        name: "Dashboard",
        hash: HOME_CONTACT_HASH,
      });
    }

    window.dispatchEvent(new CustomEvent("dashboard-scroll-to-contact"));
    return;
  }

  await router.push({
    name: "Dashboard",
    hash: HOME_CONTACT_HASH,
  });
};

// Buy Now 按钮点击
const handleBuyNow = () => {
  navigateToHomePricing();
};

const handleBuyNowVisitor = () => {
  navigateToHomePricing();
};

// Contact
const handleContact = () => {
  navigateToHomeContact();
};

// Login
const handleLogin = () => {
  router.push("/login");
};

const handleHomeUserEntryClick = () => {
  if (isDashboardHeader.value && isAuthed.value) {
    router.push("/project-manage");
    return;
  }

  handleLogin();
};

// Upgrade 按钮点击（付费用户）
const handleUpgrade = () => {
  navigateToHomePricing();
};

const closeUserDropdown = () => {
  isDropdownOpen.value = false;
  languageSubmenuOpen.value = false;
};

// 账户菜单点击
const handleAccount = async () => {
  closeUserDropdown();
  accountDrawerOpen.value = true;
  accountDrawerLoading.value = true;
  const ok = await authStore.fetchProfile();
  accountDrawerLoading.value = false;
  if (!ok && !authStore.user) {
    accountDrawerOpen.value = false;
    message.error(t("accountSetting.loadFailed"));
    return;
  }
};

const handleAccountDrawerCancel = () => {
  if (accountDrawerSubmitting.value) {
    return;
  }
  accountDrawerOpen.value = false;
};

const handleAccountDrawerConfirm = async (payload) => {
  const userId = authStore.user?.id || authStore.user?.userId;
  if (!userId) {
    message.error(t("accountSetting.updateFailed"));
    return;
  }

  accountDrawerSubmitting.value = true;
  try {
    const response = await updateUser({
      id: userId,
      nickName: payload.nickname,
      email:
        authStore.user?.emailAddress || authStore.user?.email || payload.email,
      status: authStore.user?.status || "0",
      profileIcon: payload.profileIcon || "",
      password: payload.password || "",
      company: authStore.user?.company || payload.company || "",
      industry: authStore.user?.industry || payload.industry || "",
    });

    if (response?.code !== 200) {
      throw new Error(response?.msg || "update failed");
    }

    await authStore.fetchProfile();
    message.success(t("accountSetting.updateSuccess"));
    accountDrawerOpen.value = false;
  } catch (_error) {
    message.error(t("accountSetting.updateFailed"));
  } finally {
    accountDrawerSubmitting.value = false;
  }
};

// User Manage 菜单点击（组织管理员）
const handleUserManage = () => {
  closeUserDropdown();
  router.push("/user-manage");
};

// 语言子菜单展开/收起
const languageSubmenuOpen = ref(false);

const toggleLanguageSubmenu = () => {
  languageSubmenuOpen.value = !languageSubmenuOpen.value;
};

// Demo 菜单点击
const handleDemo = () => {
  closeUserDropdown();
  router.push("/demo");
};

// 语言切换
const onLangClick = (langCode) => {
  locale.value = langCode;
  appStore.setLang(langCode);
};

// 退出登录
const onLogout = async () => {
  closeUserDropdown();
  await authStore.logout();
  router.replace("/login");
};

// 下拉框显示状态变化
const onDropdownVisibleChange = (visible) => {
  isDropdownOpen.value = visible;
  if (!visible) {
    languageSubmenuOpen.value = false;
  }
};

const handleResize = () => {
  if (showTaskPanel.value) {
    updateTaskPanelPosition();
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  loadRunningTasks();
  startTaskRefreshTimer();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  clearTaskPanelHideTimer();
  clearTaskRefreshTimer();
});

const handleBrandClick = () => {
  router.push("/");
};

watch(
  () => route.fullPath,
  () => {
    closeUserDropdown();
  },
);

watch(
  () => authStore.isAuthed,
  (isAuthedNow) => {
    if (!isAuthedNow) {
      showTaskPanel.value = false;
      runningTasks.value = [];
      clearTaskRefreshTimer();
      return;
    }

    loadRunningTasks();
    startTaskRefreshTimer();
  },
);

watch(taskCount, (count) => {
  if (count <= 0) {
    showTaskPanel.value = false;
    clearTaskPanelHideTimer();
  }
});
</script>

<style scoped lang="less">
.home-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background-color: rgba(50, 51, 53, 1);
  width: 100%;
}
/* ... existing styles ... */

.home-header__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 41px;
  margin: 13px 0 0 26px;
  padding-right: 26px;
}

/* 左侧品牌区域 */
.brand-section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 78px;
  height: 41px;
  cursor: pointer;
}

.brand-logo {
  width: 90px;
  height: 45px;
  position: relative;
  top: -3px;
}

/* 右侧功能区域 */
.function-section {
  display: flex;
  align-items: center;
  gap: 20px; /* 默认间距 */
}

/* 未登录状态图标按钮 */
.header-icon-btn {
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 34px;
  padding: 0 10px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  img {
    margin-right: 6px;
    height: 24px; 
    width: auto;
  }

  span {
    max-width: 160px;
    display: inline-block;
    color: #fff;
    font-size: 14px;
    font-family: "Arial", sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.language-btn {
  position: relative;
  &:hover .lang-dropdown {
    display: block;
  }
}

.lang-dropdown {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  background: #333;
  border-radius: 4px;
  padding: 5px 0;
  min-width: 100px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.lang-item {
  padding: 8px 15px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    color: rgba(3, 239, 107, 1);
  }
}

/* Buy Now 按钮（试用用户） */
.buy-now-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 17px;
  width: 96px;
  height: 34px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }
}

.buy-now-text {
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "Arial-BoldMT", "Arial", sans-serif;
  font-weight: bold;
  white-space: nowrap;
  line-height: 16px;
}

/* 存储进度背景（付费用户） */
.storage-bg {
  display: flex;
  align-items: center;
  width: 290px;
  height: 32px;
  margin: 3px 0 0 0;
  background: url("@/assets/imgs/header/storage-bg.png") 100% no-repeat;
  background-size: 100% 100%;
  cursor: pointer;
}

.storage-icon {
  width: 16px;
  height: 16px;
  margin-left: 18px;
  flex-shrink: 0;
}

.storage-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100px;
  margin-left: 8px;
  flex-shrink: 0;
}

.storage-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 14px;
}

.storage-percent {
  color: rgba(3, 239, 107, 1);
  font-size: 12px;
  font-family: "Arial-BoldMT", "Arial", sans-serif;
  font-weight: bold;
  white-space: nowrap;
  line-height: 14px;
}

.storage-capacity {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-family: "Arial-BoldMT", "Arial", sans-serif;
  font-weight: bold;
  white-space: nowrap;
  line-height: 14px;
}

.storage-progress {
  width: 100px;
  height: 2px;
  margin-top: 4px;
  background: rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  overflow: hidden;
}

.storage-progress__fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #03ef6b 0%, #00c65a 100%);
  transition: width 0.2s ease;
}

.divider-vertical {
  width: 1px;
  height: 19px;
  margin-left: 12px;
  flex-shrink: 0;
}

.task-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.tasks-text {
  height: 16px;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "ArialMT", "Arial", sans-serif;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 11px;
  display: flex;
  align-items: center;
}

.tasks-count {
  height: 16px;
  color: rgba(3, 239, 107, 1);
  font-size: 14px;
  font-family: "Arial-BoldMT", "Arial", sans-serif;
  font-weight: bold;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 4px;
  margin-right: 21px;
  display: flex;
  align-items: center;
}

/* Upgrade 按钮（付费用户） */
.upgrade-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(44, 44, 45, 1);
  border-radius: 17px;
  min-width: 112px;
  width: auto;
  padding: 0 16px;
  height: 34px;
  margin-left: 30px;
  cursor: pointer;
  transition: background-color 0.2s;

  // &:hover {
  //   background-color: rgba(54, 54, 55, 1);
  // }
}

.upgrade-icon {
  width: 20px;
  height: 17px;
  flex-shrink: 0;
}

.upgrade-text {
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "ArialMT", "Arial", sans-serif;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 用户信息区域 */
.user-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // 默认状态：与顶部栏背景色一致
  background-color: rgba(50, 51, 53, 1);
  border-radius: 17px;
  width: 130px;
  height: 34px;
  margin-left: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  // 悬停或展开时：半透明白色背景
  &:hover,
  &--active {
    background-color: rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
}

/* 消息通知图标 */
.notification-icon {
  width: 24px;
  height: 24px;
  margin-left: 30px;
  margin-right: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
  }
}

.user-section {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  width: auto;
  max-width: 214px;
  box-sizing: border-box;
  padding: 0 12px;
  height: 34px;
  margin-left: 0; /* 由 notification-icon 的 margin-right 控制间距 */
  cursor: pointer;
  transition: all 0.2s ease;

  // 悬停或展开时：颜色加深或保持
  &:hover,
  &--active {
    background-color: rgba(255, 255, 255, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
}

.user-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.user-name {
  height: 16px;
  max-width: 160px;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "ArialMT", "Arial", sans-serif;
  text-align: center;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 6px; /* 文字与图标间距 */

  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

/* 底部分隔线 */
.divider-horizontal {
  width: 100%;
  height: 1px;
  margin-top: 9px;
  display: block;
}

/* 下拉菜单样式 */
.user-dropdown-menu {
  background-color: rgba(50, 51, 53, 1);
  width: 180px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 20px 0 17px 0;
  margin-top: 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 20px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:not(.logout-item) {
    margin-bottom: 4px;
  }
}

.menu-item-with-submenu {
  margin-bottom: 4px;
}

.menu-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.menu-text {
  flex: 1;
  height: 16px;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "Arial-BoldMT", "Arial", sans-serif;
  font-weight: bold;
  white-space: nowrap;
  line-height: 16px;
  margin-left: 12px;
  display: flex;
  align-items: center;
}

.menu-arrow-icon {
  width: 5px;
  height: 10px;
  flex-shrink: 0;
  transition: transform 0.2s;

  &--expanded {
    transform: rotate(90deg);
  }
}


.logout-item {
  margin-top: 4px;
}

/* 语言子菜单样式 */
.language-submenu {
  background-color: rgba(60, 61, 63, 1);
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  margin-top: 4px;
}

.submenu-item {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 20px 0 56px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &--active {
    background-color: rgba(0, 169, 101, 0.2);

    .submenu-text {
      color: rgba(0, 169, 101, 1);
    }
  }
}

.submenu-text {
  flex: 1;
  height: 16px;
  color: rgba(255, 255, 255, 1);
  font-size: 14px;
  font-family: "ArialMT", "Arial", sans-serif;
  white-space: nowrap;
  line-height: 16px;
  display: flex;
  align-items: center;
}

/* 子菜单展开动画 */
.submenu-expand-enter-active,
.submenu-expand-leave-active {
  transition: all 0.2s ease;
  max-height: 150px;
  overflow: hidden;
}

.submenu-expand-enter-from,
.submenu-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Dev Control Panel */
.dev-control-panel {
  position: fixed;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  padding: 10px;
  border-radius: 8px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dev-title {
  color: #fff;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 5px;
}

.dev-control-panel button {
  background: #00a965;
  color: #fff;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: #008f55;
  }
}

.dev-control-panel button.active {
  background: #e6a23c; /* Orange for active state to stand out */
  color: #fff;
  font-weight: bold;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);

  &:hover {
    background: #cf9236;
  }
}
</style>
